import { MessageEntity } from "@/indexedDB/types";
import { RtfLinkType } from "@/middleware/links";

import { create } from "zustand";

type State = {
  draftMessages: Record<string, string>;
  replyMessages: Record<string, MessageEntity>;
  editMessages: Record<string, MessageEntity>;
  linkMessages: Record<string, string>;
  linkMessagesMetadata: Record<string, RtfLinkType>;
};

type Actions = {
  setDraftMessage: (chatId: string, message: string) => void;
  clearDraftMessage: (chatId: string) => void;
  setReplyMessage: (chatId: string, parentMessage: MessageEntity) => void;
  clearReplyMessage: (chatId: string) => void;
  setEditMessage: (chatId: string, message: MessageEntity) => void;
  clearEditMessage: (chatId: string) => void;
  setLinkMessage: (chatId: string, link: string) => void;
  clearLinkMessage: (chatId: string) => void;
  setLinkMessageMetadata: (chatId: string, link: RtfLinkType) => void;
  clearLinkMessageMetadata: (chatId: string) => void;
  resetRtfInputState: () => void;
};

const initialState: State = {
  draftMessages: {},
  replyMessages: {},
  editMessages: {},
  linkMessages: {},
  linkMessagesMetadata: {},
};

export const useRtfInputState = create<State & Actions>()((set) => ({
  // State
  ...initialState,

  // Actions
  setDraftMessage: (chatId, message) => {
    set((state) => ({
      draftMessages: { ...state.draftMessages, [chatId]: message },
    }));
  },

  clearDraftMessage: (chatId) =>
    set((state) => {
      const { [chatId]: _, ...rest } = state.draftMessages;
      return { draftMessages: rest };
    }),

  setReplyMessage: (chatId, parentMessage) =>
    set((state) => ({
      replyMessages: { ...state.replyMessages, [chatId]: parentMessage },
    })),

  clearReplyMessage: (chatId) =>
    set((state) => {
      const { [chatId]: _, ...rest } = state.replyMessages;
      return { replyMessages: rest };
    }),

  setEditMessage: (chatId, message) =>
    set((state) => ({
      editMessages: { ...state.editMessages, [chatId]: message },
    })),

  clearEditMessage: (chatId) =>
    set((state) => {
      const { [chatId]: _, ...rest } = state.editMessages;
      return { editMessages: rest };
    }),

  setLinkMessage: (chatId, link) =>
    set((state) => ({
      linkMessages: { ...state.linkMessages, [chatId]: link },
    })),

  clearLinkMessage: (chatId) =>
    set((state) => {
      const { [chatId]: _, ...rest } = state.linkMessages;
      return { linkMessages: rest };
    }),

  setLinkMessageMetadata: (chatId, linkMetadata) =>
    set((state) => ({
      linkMessagesMetadata: {
        ...state.linkMessagesMetadata,
        [chatId]: linkMetadata,
      },
    })),

  clearLinkMessageMetadata: (chatId) =>
    set((state) => {
      const { [chatId]: _, ...rest } = state.linkMessagesMetadata;
      return { linkMessagesMetadata: rest };
    }),

  resetRtfInputState: () => {
    set(initialState);
  },
}));
